import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useFeatureEnabled } from './FeatureFlagProvider';
import { CORE_FEATURES } from './types';

interface FeatureGuardProps {
  feature: keyof typeof CORE_FEATURES;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  showDebugInfo?: boolean;
}

export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  feature,
  children,
  fallback = null,
  loadingComponent = null,
  showDebugInfo = __DEV__,
}) => {
  const featureKey = CORE_FEATURES[feature];
  const isEnabled = useFeatureEnabled(featureKey);

  // Show debug info in development
  if (showDebugInfo && __DEV__) {
    console.log(`🚩 FeatureGuard: ${feature} (${featureKey}) = ${isEnabled}`);
  }

  if (loadingComponent) {
    // You could add loading state logic here if needed
    // For now, we'll just proceed with the feature check
  }

  return isEnabled ? <>{children}</> : <>{fallback}</>;
};

// Specific guards for the 4 core features
export const EnhancedDashboardGuard: React.FC<Omit<FeatureGuardProps, 'feature'>> = (props) => (
  <FeatureGuard feature="ENHANCED_DASHBOARD" {...props} />
);

export const AdvancedDriverManagementGuard: React.FC<Omit<FeatureGuardProps, 'feature'>> = (props) => (
  <FeatureGuard feature="ADVANCED_DRIVER_MANAGEMENT" {...props} />
);

export const ComprehensiveVehicleTrackingGuard: React.FC<Omit<FeatureGuardProps, 'feature'>> = (props) => (
  <FeatureGuard feature="COMPREHENSIVE_VEHICLE_TRACKING" {...props} />
);

export const DetailedAssignmentHistoryGuard: React.FC<Omit<FeatureGuardProps, 'feature'>> = (props) => (
  <FeatureGuard feature="DETAILED_ASSIGNMENT_HISTORY" {...props} />
);

// Coming Soon Component for disabled features
interface ComingSoonProps {
  featureName: string;
  description?: string;
  estimatedRelease?: string;
}

export const ComingSoon: React.FC<ComingSoonProps> = ({
  featureName,
  description,
  estimatedRelease,
}) => (
  <View style={styles.comingSoonContainer}>
    <Text style={styles.comingSoonTitle}>🚀 {featureName}</Text>
    <Text style={styles.comingSoonSubtitle}>Coming Soon</Text>
    {description && (
      <Text style={styles.comingSoonDescription}>{description}</Text>
    )}
    {estimatedRelease && (
      <Text style={styles.comingSoonRelease}>Expected: {estimatedRelease}</Text>
    )}
  </View>
);

// Feature Toggle Button for admins (development only)
interface FeatureToggleProps {
  feature: keyof typeof CORE_FEATURES;
  label?: string;
}

export const FeatureToggle: React.FC<FeatureToggleProps> = ({
  feature,
  label,
}) => {
  if (!__DEV__) return null;

  const featureKey = CORE_FEATURES[feature];
  const isEnabled = useFeatureEnabled(featureKey);

  return (
    <View style={styles.toggleContainer}>
      <Text style={styles.toggleLabel}>
        🚩 {label || feature}: {isEnabled ? '✅' : '❌'}
      </Text>
    </View>
  );
};

// Debug Panel showing all feature states
export const FeatureDebugPanel: React.FC = () => {
  if (!__DEV__) return null;

  return (
    <View style={styles.debugPanel}>
      <Text style={styles.debugTitle}>🚩 Feature Flags</Text>
      {Object.entries(CORE_FEATURES).map(([key, value]) => (
        <FeatureToggle key={key} feature={key as keyof typeof CORE_FEATURES} />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  comingSoonContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: 10,
    margin: 10,
  },
  comingSoonTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 5,
  },
  comingSoonSubtitle: {
    fontSize: 16,
    color: '#7f8c8d',
    marginBottom: 10,
  },
  comingSoonDescription: {
    fontSize: 14,
    color: '#95a5a6',
    textAlign: 'center',
    marginBottom: 10,
  },
  comingSoonRelease: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '600',
  },
  toggleContainer: {
    padding: 5,
    margin: 2,
    backgroundColor: '#f0f0f0',
    borderRadius: 5,
  },
  toggleLabel: {
    fontSize: 12,
    fontFamily: 'monospace',
  },
  debugPanel: {
    position: 'absolute',
    top: 100,
    right: 10,
    backgroundColor: 'rgba(0,0,0,0.8)',
    padding: 10,
    borderRadius: 5,
    zIndex: 9999,
  },
  debugTitle: {
    color: 'white',
    fontWeight: 'bold',
    marginBottom: 5,
    fontSize: 14,
  },
});
